/**
 * Company Settings Page
 * Tabbed interface with Profile, Notifications, Display tabs.
 * Per CONTEXT.md: URL state for tabs, unsaved changes warning.
 */

import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCompanySettings } from '../../hooks/useCompanySettings';
import { useCompany } from '../../hooks/useCompany';
import { useToast } from '../../components/common/ToastProvider';
import PageHeader from '../../components/common/PageHeader';
import ProfileTab from '../../components/settings/ProfileTab';
import NotificationsTab from '../../components/settings/NotificationsTab';
import DisplayTab from '../../components/settings/DisplayTab';
import ImageCropModal from '../../components/settings/ImageCropModal';
import type { CompanyProfile, NotificationPreferences, DisplayPreferences } from '../../types/company';

type TabType = 'profile' | 'notifications' | 'display';

// Default notification preferences
const defaultNotificationPrefs: NotificationPreferences = {
  emailTeamActivity: true,
  emailListingEvents: true,
  inAppTeamActivity: true,
  inAppListingEvents: true,
};

// Load display preferences from localStorage
function loadDisplayPrefs(): DisplayPreferences {
  return {
    language: (localStorage.getItem('safaqat24.display.language') as 'en' | 'ar') || 'en',
    theme: (localStorage.getItem('safaqat24.display.theme') as 'light' | 'dark' | 'system') || 'system',
  };
}

export default function SettingsPage() {
  const { t, i18n } = useTranslation('companySettings');
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToast();
  const { myRole, company: selectedCompany } = useCompany();
  const {
    profile,
    settings,
    isLoading,
    isSaving,
    isUploadingLogo,
    error,
    saveProfile,
    saveLogo,
    removeLogo,
    saveSettings,
  } = useCompanySettings();

  // Tab state from URL
  const activeTab = (searchParams.get('tab') as TabType) || 'profile';
  const handleTabChange = (tab: TabType) => setSearchParams({ tab });

  // Form state (kept in parent to persist across tab switches per RESEARCH.md pitfall #1)
  const [profileFormData, setProfileFormData] = useState<Partial<CompanyProfile>>({});
  const [notificationPrefs, setNotificationPrefs] = useState<NotificationPreferences>(defaultNotificationPrefs);
  const [displayPrefs, setDisplayPrefs] = useState<DisplayPreferences>(loadDisplayPrefs);
  const [profileErrors, setProfileErrors] = useState<Record<string, string>>({});

  // Crop modal state
  const [cropFile, setCropFile] = useState<File | null>(null);
  const [showCropModal, setShowCropModal] = useState(false);

  // Unsaved changes dialog state
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingTab, setPendingTab] = useState<TabType | null>(null);

  // Initialize form data from profile
  useEffect(() => {
    if (profile) {
      setProfileFormData({
        name: profile.name,
        nameAr: profile.nameAr,
        email: profile.email,
        phone: profile.phone,
        address: profile.address,
        description: profile.description,
        descriptionAr: profile.descriptionAr,
        website: profile.website,
      });
    }
  }, [profile]);

  // Initialize notification prefs from settings
  useEffect(() => {
    if (settings?.notifications) {
      setNotificationPrefs(settings.notifications);
    }
  }, [settings]);

  // Check if user can edit (OWNER or ADMIN)
  const canEdit = myRole === 'OWNER' || myRole === 'ADMIN';

  // Track dirty state for profile
  const isProfileDirty = useMemo(() => {
    if (!profile) return false;
    return JSON.stringify(profileFormData) !== JSON.stringify({
      name: profile.name,
      nameAr: profile.nameAr,
      email: profile.email,
      phone: profile.phone,
      address: profile.address,
      description: profile.description,
      descriptionAr: profile.descriptionAr,
      website: profile.website,
    });
  }, [profile, profileFormData]);

  // Validate profile on submit
  const validateProfile = (): boolean => {
    const errors: Record<string, string> = {};

    if (!profileFormData.name || profileFormData.name.length < 2) {
      errors.name = t('profile.validation.nameMinLength');
    }

    if (profileFormData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profileFormData.email)) {
      errors.email = t('profile.validation.emailInvalid');
    }

    if (profileFormData.website && !/^https?:\/\/.+/.test(profileFormData.website)) {
      errors.website = t('profile.validation.websiteInvalid');
    }

    setProfileErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle profile save
  const handleSaveProfile = async () => {
    if (!validateProfile()) return;

    const success = await saveProfile(profileFormData);
    if (success) {
      showToast(t('profile.saved'), 'success');
    } else {
      showToast(t('profile.saveError'), 'error');
    }
  };

  // Handle notification save
  const handleSaveNotifications = async () => {
    if (!settings) return;
    const success = await saveSettings({ notifications: notificationPrefs });
    if (success) {
      showToast(t('notifications.saved'), 'success');
    } else {
      showToast(t('notifications.saveError'), 'error');
    }
  };

  // Handle logo upload flow
  const handleLogoUpload = (file: File) => {
    setCropFile(file);
    setShowCropModal(true);
  };

  const handleCropComplete = async (blob: Blob) => {
    setShowCropModal(false);
    setCropFile(null);
    const success = await saveLogo(blob);
    if (success) {
      showToast(t('profile.saved'), 'success');
    }
  };

  const handleLogoRemove = async () => {
    const success = await removeLogo();
    if (success) {
      showToast(t('profile.saved'), 'success');
    }
  };

  // Tab change with unsaved changes check
  const handleTabChangeWithCheck = (tab: TabType) => {
    if (isProfileDirty && activeTab === 'profile') {
      setPendingTab(tab);
      setShowUnsavedDialog(true);
    } else {
      handleTabChange(tab);
    }
  };

  const handleDiscardChanges = () => {
    // Reset form to original
    if (profile) {
      setProfileFormData({
        name: profile.name,
        nameAr: profile.nameAr,
        email: profile.email,
        phone: profile.phone,
        address: profile.address,
        description: profile.description,
        descriptionAr: profile.descriptionAr,
        website: profile.website,
      });
    }
    setShowUnsavedDialog(false);
    if (pendingTab) {
      handleTabChange(pendingTab);
      setPendingTab(null);
    }
  };

  const handleSaveAndContinue = async () => {
    const success = await saveProfile(profileFormData);
    setShowUnsavedDialog(false);
    if (success && pendingTab) {
      handleTabChange(pendingTab);
      setPendingTab(null);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-white" dir={i18n.dir()}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 w-32 bg-gray-200 rounded" />
            <div className="h-10 w-full bg-gray-200 rounded" />
            <div className="h-64 bg-gray-200 rounded-lg" />
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center" dir={i18n.dir()}>
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" dir={i18n.dir()}>
      <PageHeader
        title={t('title')}
        subtitle={selectedCompany?.name}
        backTo="/company/dashboard"
        backLabel={t('backToDashboard', 'Dashboard')}
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Permission warning for non-admins */}
        {!canEdit && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-800">{t('noPermission')}</p>
          </div>
        )}

        {/* Tab navigation */}
        <nav className="border-b border-gray-200 mb-8">
          <div className="flex gap-8">
            {(['profile', 'notifications', 'display'] as TabType[]).map((tab) => (
              <button
                key={tab}
                onClick={() => handleTabChangeWithCheck(tab)}
                className={`
                  pb-3 px-1 text-sm font-medium border-b-2 transition-colors
                  ${activeTab === tab
                    ? 'border-[#004D40] text-[#004D40]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                {t(`tabs.${tab}`)}
              </button>
            ))}
          </div>
        </nav>

        {/* Tab content */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          {activeTab === 'profile' && profile && (
            <ProfileTab
              profile={profile}
              formData={profileFormData}
              onChange={setProfileFormData}
              onSave={handleSaveProfile}
              onLogoUpload={handleLogoUpload}
              onLogoRemove={handleLogoRemove}
              isSaving={isSaving}
              isUploadingLogo={isUploadingLogo}
              errors={profileErrors}
              canEdit={canEdit}
            />
          )}
          {activeTab === 'notifications' && (
            <NotificationsTab
              preferences={notificationPrefs}
              onChange={setNotificationPrefs}
              onSave={handleSaveNotifications}
              isSaving={isSaving}
              canEdit={canEdit}
            />
          )}
          {activeTab === 'display' && (
            <DisplayTab
              preferences={displayPrefs}
              onChange={setDisplayPrefs}
            />
          )}
        </div>
      </div>

      {/* Image crop modal */}
      <ImageCropModal
        isOpen={showCropModal}
        file={cropFile}
        onClose={() => {
          setShowCropModal(false);
          setCropFile(null);
        }}
        onCropComplete={handleCropComplete}
      />

      {/* Unsaved changes dialog */}
      {showUnsavedDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg p-6 max-w-sm mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {t('unsavedChanges.title')}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {t('unsavedChanges.message')}
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowUnsavedDialog(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                {t('unsavedChanges.cancel')}
              </button>
              <button
                onClick={handleDiscardChanges}
                className="px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                {t('unsavedChanges.discard')}
              </button>
              <button
                onClick={handleSaveAndContinue}
                className="px-4 py-2 text-sm font-medium text-white bg-[#004D40] hover:bg-[#003930] rounded-lg transition-colors"
              >
                {t('unsavedChanges.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
